import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"

// Pages a read-only document article one PDF page at a time.
//
// A 100-page PDF parses into ~1,350 blocks, and every surface that shows the web
// article used to render all of them. The server now sends page 1 and this
// controller fetches the rest, swapping one page's blocks in place.
//
// Unlike the File Hub's pager — which defers to the PDF viewer beside it, the
// single writer of "which page are we on" — there is no viewer on these surfaces,
// so this controller owns the page outright.
export default class extends Controller {
  static targets = ["body", "label", "previous", "next", "pageInput", "loading", "error", "pageStart"]

  static values = {
    pageUrl: String,
    contentId: Number,
    pageCount: Number,
    hasUnpaged: Boolean,
    // anchor id -> the page that block sits on. Without it every in-article table
    // of contents link would resolve to an anchor that is not in the DOM.
    anchorPages: Object,
    storageKey: String,
    // The course-builder card renders its collapsed four-line peek server-side and
    // only wants a page once the reader expands it, so it holds the first load
    // back and calls start() itself.
    deferred: Boolean
  }

  static UNPAGED = "unpaged"

  connect() {
    this.page = 1
    // Guards against a slow earlier fetch overwriting a later page (see loadPage).
    this.requestToken = 0
    this.render()
    this.started = false

    if (!this.deferredValue) this.start()
  }

  // Opens on the page this reader left off at, falling back to page 1. Idempotent:
  // a card whose body is re-rendered by the generation poll calls it again.
  start() {
    const remembered = this.rememberedPage()
    const opening = remembered === null ? 1 : remembered
    this.started = true
    if (opening === this.page && !this.deferredValue) return Promise.resolve()

    this.page = opening
    this.render()
    return this.loadPage(opening)
  }

  // --- Turning pages ---

  previousPage() {
    this.goTo(this.pageBefore(this.page))
  }

  nextPage() {
    this.goTo(this.pageAfter(this.page))
  }

  // Type a number, then Enter or leave the field. An empty or out-of-range entry
  // snaps back to the current page rather than going nowhere silently.
  goToEnteredPage(event) {
    const entered = parseInt(event.currentTarget.value, 10)
    if (Number.isInteger(entered)) this.goTo(Math.max(1, Math.min(entered, this.lastPage())))

    this.render()
  }

  // Always resolves, so a caller can chain on the page actually being on screen.
  //
  // Turning a page from the foot pager otherwise leaves the reader at the BOTTOM
  // of a page they have not read a word of, so a page turn lands them at its
  // start. `scroll: false` is for the table-of-contents jump, which has its own
  // destination and must not be dragged to the top first.
  goTo(page, { scroll = true } = {}) {
    if (page === null || page === this.page) return Promise.resolve()

    this.page = page
    this.render()
    this.remember(page)

    const loaded = this.loadPage(page)
    return scroll ? loaded.then(() => this.scrollToPageStart()) : loaded
  }

  // Where the new page begins: the head pager, so the reader lands on "Page N of
  // M" with the content directly beneath it. The builder card has no head pager,
  // so there it is the top of the fetched page itself.
  //
  // Which box actually moves has to be resolved, never assumed: the academy
  // scrolls the document, the admin shell hands the page scroll to
  // `.main-content`, and the builder card scrolls its own body.
  scrollToPageStart() {
    const anchor = this.hasPageStartTarget ? this.pageStartTarget : this.bodyTarget
    const scroller = scrollContainerFor(this.bodyTarget)
    const root = document.scrollingElement || document.documentElement

    if (scroller === root) {
      anchor.scrollIntoView({ block: "start", behavior: this.scrollBehavior })
      return
    }

    // A nested scroller is moved directly rather than through scrollIntoView,
    // which walks EVERY scrollable ancestor — inside a card that would drag the
    // builder canvas behind it along too.
    const top = scroller.scrollTop +
                anchor.getBoundingClientRect().top -
                scroller.getBoundingClientRect().top
    scroller.scrollTo({ top: top, behavior: this.scrollBehavior })
  }

  get scrollBehavior() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth"
  }

  // A table-of-contents link whose target sits on another page: switch pages
  // first, then scroll to the anchor once its blocks are in the DOM. A target on
  // the page already on screen — or an anchor belonging to another document on
  // the same lesson — falls through to the browser's own jump.
  jumpToAnchor(event) {
    const link = event.target.closest("a[href^='#']")
    if (!link) return

    const anchor = link.getAttribute("href").slice(1)
    const page = this.pageForAnchor(anchor)
    if (page === null || page === this.page) return

    event.preventDefault()
    this.goTo(page, { scroll: false }).then(() => this.scrollToAnchor(anchor))
  }

  pageForAnchor(anchor) {
    const page = this.anchorPagesValue[anchor]
    if (page === undefined) return null
    if (page === this.constructor.UNPAGED) return this.constructor.UNPAGED

    const number = parseInt(page, 10)
    return Number.isInteger(number) ? number : null
  }

  scrollToAnchor(anchor) {
    const target = this.element.querySelector(`#${CSS.escape(anchor)}`)
    target?.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  // --- Fetching ---

  async loadPage(page) {
    if (!this.hasBodyTarget) return

    const token = ++this.requestToken
    this.showTransient("loading")

    try {
      // `redirect: "error"` on purpose: a surface that refuses a page answers
      // with a redirect (to sign-in, or back to the catalogue), and following it
      // would swap a whole HTML page into the article body.
      const response = await fetch(this.pageUrlFor(page), {
        headers: { Accept: "text/html" },
        redirect: "error"
      })
      if (!response.ok) throw new Error(`Page request failed (${response.status})`)

      const html = await response.text()
      // A slower earlier request must never overwrite the page the reader is on
      // now — two quick caret clicks would otherwise land on the first answer.
      if (token !== this.requestToken) return

      this.bodyTarget.innerHTML = html
      this.dispatch("page-changed", { detail: { page: this.page } })
    } catch (error) {
      if (token !== this.requestToken) return

      this.showTransient("error")
    }
  }

  retryPage() {
    this.loadPage(this.page)
  }

  pageUrlFor(page) {
    const url = new URL(this.pageUrlValue, window.location.origin)
    // The builder card's URL already names its placement; only the article
    // surfaces address a document by content id.
    if (this.hasContentIdValue) url.searchParams.set("content_id", this.contentIdValue)
    url.searchParams.set("page", page)
    return url.toString()
  }

  // The loading spinner and the failure notice are real markup in the view,
  // cloned here, so their look stays with the rest of the article's styling.
  showTransient(name) {
    const template = name === "loading" ? this.loadingTarget : this.errorTarget
    this.bodyTarget.replaceChildren(template.content.cloneNode(true))
  }

  // --- Remembering the open page across a reload ---

  // Storage is per document, because a lesson page can hold several. Every access
  // is guarded: a private window, cleared site data or a browser set to block
  // storage must render page 1 rather than throw.
  rememberedPage() {
    if (!this.hasStorageKeyValue) return null

    let stored = null
    try {
      stored = window.localStorage.getItem(this.storageKeyValue)
    } catch (error) {
      return null
    }
    if (stored === null) return null
    if (stored === this.constructor.UNPAGED) return this.hasUnpagedValue ? stored : null

    const page = parseInt(stored, 10)
    if (!Number.isInteger(page) || page < 1 || page > this.lastPage()) return null

    return page
  }

  remember(page) {
    if (!this.hasStorageKeyValue) return

    try {
      window.localStorage.setItem(this.storageKeyValue, String(page))
    } catch (error) {
      // Nothing to do: the page still turns, it just will not be remembered.
    }
  }

  // --- Rendering ---

  render() {
    this.element.dataset.articlePagerPage = String(this.page)
    this.labelTargets.forEach((label) => { label.textContent = this.pageLabel() })
    this.previousTargets.forEach((button) => { button.disabled = this.pageBefore(this.page) === null })
    this.nextTargets.forEach((button) => { button.disabled = this.pageAfter(this.page) === null })
    this.renderPageInputs()
    this.dispatch("page-changed", { detail: { page: this.page } })
  }

  // The unpaged pseudo-page has no number, so the field empties rather than
  // showing a page the reader is not on.
  renderPageInputs() {
    this.pageInputTargets.forEach((input) => {
      input.max = this.lastPage()
      input.value = this.page === this.constructor.UNPAGED ? "" : this.page
    })
  }

  pageLabel() {
    if (this.page === this.constructor.UNPAGED) return "Unpaged content"

    return `Page ${this.page} of ${this.lastPage()}`
  }

  // --- The page sequence: 1..N, then the unpaged pseudo-page when it exists ---

  pageBefore(page) {
    if (page === this.constructor.UNPAGED) return this.lastPage()
    if (page <= 1) return null

    return page - 1
  }

  pageAfter(page) {
    if (page === this.constructor.UNPAGED) return null
    if (page < this.lastPage()) return page + 1

    return this.hasUnpagedValue ? this.constructor.UNPAGED : null
  }

  lastPage() {
    return Math.max(this.pageCountValue, 1)
  }
}
